import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import Map, { Layer, Source } from 'react-map-gl/maplibre'
import type { MapRef } from 'react-map-gl/maplibre'
import {
  SEQUENTIAL_RAMP,
  buildChoroplethGeoJson,
  featureCollectionBounds,
  fillColorExpression,
} from '../analyticsMap/choropleth'
import { DEFAULT_VIEW_STATE, getMapStyle } from '../../lib/mapConfig'
import { valuedFeaturesForFit } from './geometry'
import { useGeoUnits } from '../../hooks/useGeoAnalytics'
import type { GeoMetricRow, MetricOption } from '../../types/geoAnalytics'

// Synthetic sequential metric so we can reuse the slice-1 choropleth builder + ramp for
// ∂margin/∂effort sensitivity — a unitless quantity with no k-anonymity basis (never suppressed).
const SENSITIVITY_METRIC: MetricOption = {
  key: 'sensitivity',
  label: 'Sensitivity',
  kind: 'sequential',
  domain: [0, 1],
  format: 'count',
  group: 'union',
  description: 'Marginal margin per unit of canvassing effort.',
}

export interface SensitivityMapPaneProps {
  state: string
  sensitivities: { geoUnitId: string; value: number }[]
}

/**
 * Sensitivity-by-precinct choropleth (spec §slice-4): merges per-unit ∂margin/∂effort values
 * into the state's precinct polygons and colors them on the sequential ramp. Reuses the slice-1
 * choropleth primitives; renders an empty state when no precinct geometry is loaded.
 */
export function SensitivityMapPane({ state, sensitivities }: SensitivityMapPaneProps) {
  const mapRef = useRef<MapRef>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // No vintage: /geo/units resolves MAX(VintageYear) for precinct+state server-side. Params must
  // stay identical to ScenarioWorkspacePage's precinct query so React Query dedupes the fetch.
  const unitsQuery = useGeoUnits(
    state ? { type: 'precinct', state } : null,
  )
  const units = unitsQuery.data

  const values = sensitivities.map((s) => s.value)
  const domain = useMemo<[number, number]>(() => {
    if (values.length === 0) return [0, 1]
    const min = Math.min(...values)
    const max = Math.max(...values)
    return min === max ? [min, min + 1] : [min, max]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensitivities])

  const geojson = useMemo(() => {
    if (!units) return null
    const rows: GeoMetricRow[] = sensitivities.map((s) => ({
      geoUnitId: s.geoUnitId,
      metricKey: 'sensitivity',
      value: s.value,
      suppressed: false,
    }))
    return buildChoroplethGeoJson(units, rows, SENSITIVITY_METRIC)
  }, [units, sensitivities])

  const fillColor = useMemo(() => fillColorExpression(SENSITIVITY_METRIC, domain), [domain])
  // Fit to the contest's precincts (those carrying a sensitivity value), not the whole state's
  // precinct layer — a district contest otherwise framed all of Virginia (2026-10-23 live bug 8d).
  const bounds = useMemo(() => featureCollectionBounds(valuedFeaturesForFit(geojson)), [geojson])

  useEffect(() => {
    if (!bounds || !mapLoaded) return
    mapRef.current?.getMap()?.fitBounds(bounds, { padding: 30, maxZoom: 13, duration: 800 })
  }, [bounds, mapLoaded])

  const hasGeometry = !!units && units.features.length > 0
  // No sensitivity values means there's nothing to color — a run with no modeled precinct results
  // (e.g. a top-of-ticket / no-returns contest). Show an explicit empty state instead of a
  // live-but-empty basemap, which reads as a broken map (findings D3).
  const hasSensitivity = sensitivities.length > 0
  const showMap = !unitsQuery.isLoading && hasGeometry && hasSensitivity

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
        Where effort moves the needle
      </Text>
      <Text fontWeight="semibold" fontSize="sm" mb={2}>
        Sensitivity by precinct
      </Text>

      <Box position="relative" h="260px" borderRadius="md" overflow="hidden">
        {unitsQuery.isLoading ? (
          <Center h="full">
            <Spinner size="lg" color="accent" />
          </Center>
        ) : !hasGeometry ? (
          <Center h="full" bg="gray.50">
            <Text fontSize="sm" color="gray.500">
              No precinct geometries loaded for {state || 'this contest'}.
            </Text>
          </Center>
        ) : !hasSensitivity ? (
          <Center h="full" bg="gray.50" px={6}>
            <Box textAlign="center">
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                No sensitivity estimates for this contest yet
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                They appear once precinct-level results are modeled for this race.
              </Text>
            </Box>
          </Center>
        ) : (
          <Map
            ref={mapRef}
            initialViewState={DEFAULT_VIEW_STATE}
            onLoad={() => setMapLoaded(true)}
            mapStyle={getMapStyle()}
            style={{ width: '100%', height: '100%' }}
          >
            {geojson && (
              <Source id="sensitivity-source" type="geojson" data={geojson} promoteId="id">
                <Layer
                  id="sensitivity-fill"
                  type="fill"
                  paint={{ 'fill-color': fillColor as never, 'fill-opacity': 0.8 }}
                />
                <Layer
                  id="sensitivity-line"
                  type="line"
                  paint={{ 'line-color': '#FFFFFF', 'line-width': 1 }}
                />
              </Source>
            )}
          </Map>
        )}
      </Box>

      {showMap && (
        <Flex align="center" gap={2} mt={3} fontSize="xs" color="gray.600">
          <Box w="14px" h="14px" borderRadius="sm" bg={SEQUENTIAL_RAMP[0]} borderWidth="1px" />
          <Text>low ∂margin/∂effort</Text>
          <Box w="14px" h="14px" borderRadius="sm" bg={SEQUENTIAL_RAMP[1]} ml={2} />
          <Text>high</Text>
        </Flex>
      )}
    </Box>
  )
}
